import {
	DatePicker,
	GroupInput,
	IDatePickerProps,
	ISelect,
	Select,
} from '@/components';
import { DEFAULT_YEARS_SELECT, TIME_OPTIONS, TimeOptionEnum } from '@/constant';
import { useYearList } from '@/hooks';
import { TYearScreenCode } from '@/services';
import {
	dayjs,
	formatDate,
	getFieldSelectProps,
	insertObjectIfV2,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';
import { memo, useCallback, useEffect } from 'react';

import { DefaultOptionType } from 'antd/es/select';

import { FormikProps, getIn } from 'formik';
import { TFunction } from 'i18next';

import Checkbox, { ICheckbox } from '../checkbox';
import { DEFAULT_YEARS } from './config';
import { EEditableFilterType, TSelectDateValue } from './type';

export const filterDateDefaultValue: TSelectDateValue = {
	type: EEditableFilterType.DATE_RANGE_PICKER,
	selectTime: '',
	selectYear: '',
	startDate: null,
	endDate: null,
	searchYes: null,
	searchNo: null,
};

export type TFilterDate = {
	t?: TFunction<'common'>;
	formik: FormikProps<{ [key: string]: any }>;
	label: string;
	name: string;
	labelClassName?: string;
	containerClassName?: string;
	screenCode?: TYearScreenCode;
	yearOptions?: DefaultOptionType[];
	option?: {
		format?: boolean;
	};
	onSearch?: () => void;
	isHideOptionYear?: boolean;
	defaultValue?: TSelectDateValue;
	searchYesNo?: boolean;
	checkboxProps?: {
		yes?: Partial<ICheckbox>;
		no?: Partial<ICheckbox>;
	};
	selectProps?: {
		time?: Partial<ISelect>;
		year?: Partial<ISelect>;
	};
	dateProps?: {
		startDate?: Partial<IDatePickerProps> & {
			getAdditionalValues?: (
				value: any,
				prevValues: any
			) => Partial<any> | void;
		};
		endDate?: Partial<IDatePickerProps> & {
			getAdditionalValues?: (
				value: any,
				prevValues: any
			) => Partial<any> | void;
		};
	};
};

const FilterDate = ({
	t,
	formik,
	label,
	name,
	labelClassName,
	containerClassName,
	screenCode,
	yearOptions,
	onSearch,
	isHideOptionYear,
	defaultValue = filterDateDefaultValue,
	searchYesNo,
	checkboxProps,
	selectProps,
	dateProps,
}: TFilterDate) => {
	const yearList = useYearList(screenCode);
	useEffect(() => {
		if (formik.values[name]?.selectTime) {
			formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
				...prev,
				[name]: {
					...prev[name],
					selectYear: '',
					searchYes: null,
					searchNo: null,
				},
			}));
		}
	}, [formik.values[name]?.selectTime]);

	useEffect(() => {
		if (formik.values[name]?.selectYear) {
			formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
				...prev,
				[name]: {
					...prev[name],
					selectTime: '',
					searchYes: null,
					searchNo: null,
				},
			}));
		}
	}, [formik.values[name]?.selectYear]);

	const handleSelectTimeChange = useCallback(
		(value: TimeOptionEnum) => {
			const today = dayjs();

			switch (value) {
				case TimeOptionEnum.TODAY:
					formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
						...prev,
						[name]: {
							...prev[name],
							selectTime: value,
							startDate: formatDate(today),
							endDate: formatDate(today),
							searchYes: null,
							searchNo: null,
						},
					}));
					break;
				case TimeOptionEnum.THIS_WEEK:
					formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
						...prev,
						[name]: {
							...prev[name],
							selectTime: value,
							startDate: formatDate(today.startOf('week')),
							endDate: formatDate(today.endOf('week')),
							searchYes: null,
							searchNo: null,
						},
					}));
					break;
				case TimeOptionEnum.THIS_MONTH:
					formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
						...prev,
						[name]: {
							...prev[name],
							selectTime: value,
							startDate: formatDate(today.startOf('month')),
							endDate: formatDate(today.endOf('month')),
							searchYes: null,
							searchNo: null,
						},
					}));
					break;
				case TimeOptionEnum.THIS_YEAR:
					formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
						...prev,
						[name]: {
							...prev[name],
							selectTime: value,
							startDate: formatDate(today.startOf('year')),
							endDate: formatDate(today.endOf('year')),
							searchYes: null,
							searchNo: null,
						},
					}));
					break;
				default:
					formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
						...prev,
						[name]: {
							...prev[name],
							selectTime: value,
							startDate: null,
							endDate: null,
							searchYes: null,
							searchNo: null,
						},
					}));
					break;
			}

			onSearch?.();
		},
		[formik, onSearch]
	);

	const handleSelectYear = useCallback(
		(value: string) => {
			formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
				...prev,
				[name]: {
					...prev[name],
					selectYear: value,
					startDate: value ? `${value}.01.01` : '',
					endDate: value ? `${value}.12.31` : '',
					searchYes: null,
					searchNo: null,
				},
			}));
			onSearch?.();
		},
		[formik, onSearch]
	);

	const _onChangeCheckboxYes = useCallback(
		(e: any) => {
			formik.setValues((prev) => ({
				...prev,
				[name]: {
					...prev[name],
					...filterDateDefaultValue,
					searchYes: e?.target?.checked || null,
				},
			}));
			onSearch?.();
		},
		[formik, onSearch]
	);

	const _onChangeCheckboxNo = useCallback(
		(e: any) => {
			formik.setValues((prev) => ({
				...prev,
				[name]: {
					...prev[name],
					...filterDateDefaultValue,
					searchNo: e?.target?.checked || null,
				},
			}));
			onSearch?.();
		},
		[formik, onSearch]
	);

	return (
		<GroupInput
			label={label}
			labelClassName={labelClassName}
			containerClassName={containerClassName}
			groupClassName="flex-none gap-x-1.5"
		>
			{!isHideOptionYear && (
				<>
					<>
						<Select
							containerClassName="w-[200px]"
							{...getFieldSelectProps(formik, `${name}.selectTime`, {
								defaultValue: defaultValue?.selectTime,
							})}
							options={TIME_OPTIONS}
							onChange={handleSelectTimeChange}
							placeholder={t?.('filter-date.select-period')}
							{...selectProps?.time}
						/>
					</>
					<>
						<Select
							containerClassName="w-[200px] mr-1.5"
							{...getFieldSelectProps(formik, `${name}.selectYear`, {
								defaultValue: defaultValue.selectYear,
							})}
							options={[
								DEFAULT_YEARS_SELECT,
								...(screenCode ? yearList.data : yearOptions || DEFAULT_YEARS),
							]}
							onChange={handleSelectYear}
							placeholder={t?.('filter-date.select-year')}
							{...selectProps?.year}
						/>
					</>
				</>
			)}
			<>
				<DatePicker
					containerClassName="w-[140px]"
					name={`${name}.startDate`}
					defaultValue={defaultValue?.startDate}
					value={
						getIn(formik?.values, `${name}.startDate`)
							? dayjs(
									getIn(formik?.values, `${name}.startDate`),
									SUPPORT_FORMAT_DATE_LIST
								)
							: null
					}
					onChange={(date: dayjs.Dayjs, dateString: string | string[]) => {
						formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
							...prev,
							[name]: {
								...prev[name],
								startDate: date,
								selectTime: '',
								selectYear: '',
								...(dateProps?.startDate?.getAdditionalValues
									? dateProps?.startDate?.getAdditionalValues(date, prev) ?? {}
									: {}),
							},
						}));
						onSearch?.();
					}}
					{...insertObjectIfV2(formik?.values?.[`${name}`]?.endDate, {
						maxDate: dayjs(
							getIn(formik?.values, `${name}.endDate`),
							SUPPORT_FORMAT_DATE_LIST
						),
					})}
					{...dateProps?.startDate}
				/>
			</>
			<span className="text-[13px] text-font4">~</span>
			<>
				<DatePicker
					containerClassName="w-[140px] mr-1.5"
					name={`${name}.endDate`}
					defaultValue={defaultValue?.endDate}
					value={
						getIn(formik?.values, `${name}.endDate`)
							? dayjs(
									getIn(formik?.values, `${name}.endDate`),
									SUPPORT_FORMAT_DATE_LIST
								)
							: null
					}
					onChange={(date: dayjs.Dayjs, dateString: string | string[]) => {
						formik.setValues((prev: { [name: string]: TSelectDateValue }) => ({
							...prev,
							[name]: {
								...prev[name],
								endDate: date,
								selectTime: '',
								selectYear: '',
								...(dateProps?.endDate?.getAdditionalValues
									? dateProps?.endDate?.getAdditionalValues(date, prev) ?? {}
									: {}),
							},
						}));
						onSearch?.();
					}}
					{...insertObjectIfV2(formik?.values?.[`${name}`]?.startDate, {
						minDate: dayjs(
							getIn(formik?.values, `${name}.startDate`),
							SUPPORT_FORMAT_DATE_LIST
						),
					})}
					{...dateProps?.endDate}
				/>
			</>

			{searchYesNo && (
				<>
					<Checkbox
						checked={formik.values[name]?.searchYes}
						onChange={_onChangeCheckboxYes}
						label="유"
						labelClassName="min-w-min"
						{...checkboxProps?.yes}
					/>
					<Checkbox
						checked={formik.values[name]?.searchNo}
						onChange={_onChangeCheckboxNo}
						label="무"
						labelClassName="min-w-min"
						containerClassName="ml-1"
						{...checkboxProps?.no}
					/>
				</>
			)}
		</GroupInput>
	);
};

export default memo(FilterDate);
